import { useEffect, useState } from "react";
import { api } from "../../utils/api.js";
import { useAuthStore } from "../../store/auth.js";

interface SettingMeta {
  label: string;
  group: string;
  placeholder: string;
  sensitive: boolean;
}

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const [values, setValues] = useState<Record<string, string>>({});
  const [meta, setMeta] = useState<Record<string, SettingMeta>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{
        values: Record<string, string | null>;
        meta: Record<string, SettingMeta>;
      }>("/settings")
      .then((data) => {
        setMeta(data.meta);
        const v: Record<string, string> = {};
        for (const [k, val] of Object.entries(data.values)) {
          v[k] = val ?? "";
        }
        setValues(v);
      });
  }, []);

  const groups: Record<string, string[]> = {};
  for (const [key, m] of Object.entries(meta)) {
    (groups[m.group] ??= []).push(key);
  }

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      await api.put("/settings", values);
      setMessage("Settings saved");
    } catch {
      setMessage("Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500">Manage application configuration</p>
      </div>

      {Object.entries(groups).map(([group, keys]) => (
        <div key={group} className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">{group}</h2>
          <div className="space-y-4">
            {keys.map((key) => {
              const m = meta[key]!;
              return (
                <div key={key}>
                  <label htmlFor={key} className="block text-sm font-medium text-gray-700 mb-1.5">
                    {m.label}
                  </label>
                  <input
                    id={key}
                    type={m.sensitive ? "password" : "text"}
                    placeholder={m.placeholder}
                    value={values[key] ?? ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                    disabled={!user?.isAdmin}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {user?.isAdmin && (
        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
          {message && (
            <span
              className={`text-sm ${message.includes("Failed") ? "text-red-600" : "text-green-600"}`}
            >
              {message}
            </span>
          )}
        </div>
      )}

      {!user?.isAdmin && <p className="text-sm text-gray-500">Only admins can modify settings.</p>}
    </div>
  );
}
